import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";

/** Tipos locales */
type Task = {
  id: string; title: string; description: string | null;
  priority: "Alta" | "Media" | "Baja"; deadline: string | null; status: "todo"|"in-progress"|"done";
  project_id: string; assignee_id?: string|null;
};
type Project = { id: string; company_id: string|null };
type Label = { id: string; name: string; color: string; company_id: string };

export default function TaskDetailsModal({
  task, onClose, onUpdated
}: {
  task: Task;
  onClose: () => void;
  onUpdated?: (task: Task) => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? "");
  const [priority, setPriority] = useState(task.priority);
  const [deadline, setDeadline] = useState<string>(task.deadline ?? "");
  const [status, setStatus] = useState<Task["status"]>(task.status);
  const [assignee, setAssignee] = useState<string>(task.assignee_id ?? "");

  const [companyId, setCompanyId] = useState<string | null>(null);

  // etiquetas
  const [labels, setLabels] = useState<Label[]>([]);
  const [labelsAvailable, setLabelsAvailable] = useState<boolean>(true);
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState("#6366f1");

  useEffect(() => {
    (async () => {
      const { data: proj } = await supabase.from("projects").select("id, company_id").eq("id", task.project_id).single();
      setCompanyId((proj as Project)?.company_id ?? null);
    })();
  }, [task.project_id]);

  // etiquetas empresa + asignadas
  useEffect(() => {
    (async () => {
      if (!companyId) { setLabels([]); return; }
      const res = await supabase.from("labels").select("id,name,color,company_id").eq("company_id", companyId).order("name");
      if ((res as any)?.error) { setLabelsAvailable(false); setLabels([]); return; }
      setLabels(((res as any)?.data ?? []) as Label[]);
      setLabelsAvailable(true);

      const res2 = await supabase.from("task_labels").select("label_id").eq("task_id", task.id);
      if ((res2 as any)?.error) { setSelectedLabelIds([]); return; }
      setSelectedLabelIds(((res2 as any)?.data ?? []).map((r:any)=>r.label_id));
    })();
  }, [companyId, task.id]);

  const toggleLabel = async (labelId: string) => {
    if (!labelsAvailable) return;
    const exists = selectedLabelIds.includes(labelId);
    if (exists) {
      await supabase.from("task_labels").delete().eq("task_id", task.id).eq("label_id", labelId);
      setSelectedLabelIds(prev => prev.filter(id => id !== labelId));
    } else {
      await supabase.from("task_labels").insert({ task_id: task.id, label_id: labelId });
      setSelectedLabelIds(prev => [...prev, labelId]);
    }
  };

  const createLabel = async () => {
    if (!companyId || !newLabelName.trim()) return;
    const payload = { name: newLabelName.trim(), color: newLabelColor, company_id: companyId };
    const { data, error } = await supabase.from("labels").insert(payload).select("*").single();
    if (error) { setLabelsAvailable(false); return; }
    setLabels(prev => [...prev, data as Label]);
    setNewLabelName("");
    setNewLabelColor("#6366f1");
  };

  const save = async () => {
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      priority, deadline: deadline || null, status,
      assignee_id: assignee || null,
    };
    const { data, error } = await supabase.from("tasks").update(payload).eq("id", task.id).select("*").single();
    if (error) return;
    onUpdated?.(data as Task);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="panel modal-panel" style={{ maxWidth: 820 }} onClick={(e)=>e.stopPropagation()}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <h3 style={{ margin:0, fontWeight:800 }}>Tarea</h3>
          <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>

        <div style={{ display:"grid", gap:12, marginTop:12 }}>
          <label style={{ display:"grid", gap:6 }}>
            <span>Título</span>
            <input className="input" value={title} onChange={e=>setTitle(e.target.value)} />
          </label>

          <label style={{ display:"grid", gap:6 }}>
            <span>Descripción</span>
            <textarea className="input" style={{ minHeight: 120, padding: 10 }} value={description} onChange={e=>setDescription(e.target.value)} />
          </label>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:12 }}>
            <label style={{ display:"grid", gap:6 }}>
              <span>Estado</span>
              <select className="input" value={status} onChange={e=>setStatus(e.target.value as any)}>
                <option value="todo">Por hacer</option>
                <option value="in-progress">En progreso</option>
                <option value="done">Hecho</option>
              </select>
            </label>
            <label style={{ display:"grid", gap:6 }}>
              <span>Prioridad</span>
              <select className="input" value={priority} onChange={e=>setPriority(e.target.value as any)}>
                <option value="Alta">Alta</option><option value="Media">Media</option><option value="Baja">Baja</option>
              </select>
            </label>
            <label style={{ display:"grid", gap:6 }}>
              <span>Fecha límite</span>
              <input type="date" className="input" value={deadline} onChange={e=>setDeadline(e.target.value)} />
            </label>
            <label style={{ display:"grid", gap:6 }}>
              <span>Asignado a</span>
              <select className="input" value={assignee} onChange={e=>setAssignee(e.target.value)}>
                <option value="">— Sin asignar —</option>
              </select>
            </label>
          </div>

          {/* Etiquetas */}
          <div className="slot" style={{ padding: 12 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: 8 }}>
              <strong>Etiquetas</strong>
              {labelsAvailable && companyId ? (
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <input className="input" placeholder="Nueva etiqueta" value={newLabelName} onChange={e=>setNewLabelName(e.target.value)} style={{ width: 160 }} />
                  <input type="color" value={newLabelColor} onChange={e=>setNewLabelColor(e.target.value)} title="Color" />
                  <button className="btn btn-secondary" onClick={createLabel}>Crear</button>
                </div>
              ) : (
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Etiquetas no disponibles. (Crea las tablas <code>labels</code> y <code>task_labels</code>).
                </span>
              )}
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {labels.map(l => (
                <button
                  key={l.id}
                  onClick={()=>toggleLabel(l.id)}
                  className="btn"
                  style={{
                    background: selectedLabelIds.includes(l.id) ? l.color : "transparent",
                    color: selectedLabelIds.includes(l.id) ? "#fff" : "var(--text)",
                    borderColor: l.color,
                    height: 28, padding: "0 10px"
                  }}
                  title={l.name}
                >
                  {l.name}
                </button>
              ))}
              {labels.length===0 && <div className="text-muted">No hay etiquetas</div>}
            </div>
          </div>

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={save}>Guardar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
